import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { actions as windowActions } from '../../actions/windowActions';
import { actions as baselineActions } from '../../actions/baselineActions';
import { actions as baselineTaskActions } from '../../actions/baselineTaskActions';
import { actions as filesActions } from '../../actions/filesActions';
import { actions as taskTagActions } from '../../actions/taskTagActions';
import BaselineTaskListDesktop from './BaselineTaskListDesktop';
import BaselineTaskListMobile from './BaselineTaskListMobile';
import FiltersDrawer from './FiltersDrawer';
import BaselineTaskDrawer from './BaselineTaskDrawer';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import {
	MOBILE_WINDOW_BREAKPOINT,
	DEFAULT_PAGE_SIZE,
	TASK_LIST_VIEWS as VIEWS,
	BASELINE_TASK_LIST_URL_PARAMS as URLParams,
} from '../../constants/constants';
import Notification from '../../components/Common/Notification';
import * as types from '../../constants/actionTypes';
import Routes from '../../constants/routes';
import _ from 'lodash';
import DocumentTitle from 'react-document-title';

export class BaselineTaskListController extends React.Component {
	constructor(props, context) {
		super(props, context);

		const initialSearchParams = {
			subscriptionId: this.props.subscriptionId,
			baselineId: this.props.urlParams.baselineId,
			departments: [],
			baselineJobTitles: [],
			communities: [],
			phases: [],
			disciplines: [],
			tags: [],
			isMobile: this.isMobile(),
			isActive: true,
			pageSize: DEFAULT_PAGE_SIZE,
			pageNumber: 1,
			sortBy: '', // Blank by default so the DataTable performs no sorting of its own, which means multiColumnSort's influence on the API will remain unchanged
			sortDir: '',
			name: '',
			additionalInfo: '',
			// Optional field only used by Baseline Task List (for now)
			multiColumnSort: [
				{
					sortBy: 'targetDaysOut',
					sortDir: 'desc',
				},
				{
					sortBy: 'targetStaticDate',
					sortDir: 'asc',
				},
				{
					sortBy: 'ordinal',
					sortDir: 'asc',
				},
			],
			createdDateStart: null,
			createdDateEnd: null,
		};

		this.state = {
			editTaskId: 0,
			baselines: [],
			columnOrder: [],
			baselineTasks: [],
			checkedTasks: [],
			allTasksChecked: false,
			totalCount: 0,
			isDrawerOpen: false,
			isFetchingBaselines: false,
			isFetchingTasks: false,
			isFetchingFilters: false,
			departments: [],
			baselineJobTitles: [],
			communities: [],
			phases: [],
			disciplines: [],
			tags: [],
			showFiltersDrawer: false,
			initialSearchParams: initialSearchParams,
			searchParams: initialSearchParams,
			lastFilterChange: new Date(),
			selectedView: VIEWS.List.value,
			formFilters: {
				isLoaded: false,
				departments: [],
				baselineJobTitles: [],
				communities: [],
				phases: [],
				taskTags: [],
				isStartDateActive: false,
				isTaskStepsActive: false,
				isTaskEffortActive: false,
				isTaskFeedbackActive: false,
				isTaskDependenciesActive: false,
				isBaselineTaskValidationRulesActive: false,
				isProjectWidgetActive: false,
				isWebhooksActive: false,
				certifiedTaskId: null,
				subscriptionFiles: [],
				events: [],
				actions: [],
			},
		};
		this.loadBaselines.bind(this);
		this.search.bind(this);
	}

	async componentDidMount() {
		await this.loadBaselines();
		await this.loadListFilters();
	}

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	loadListFilters = async () => {
		try {
			this.setState({ isFetchingFilters: true });
			const result = await this.props.baselineTaskActions.loadBaselineTaskListFilters(
				this.props.subscriptionId,
				this.state.searchParams.baselineId
			);
			const payload = result.payload;
			if (result.type === types.LOAD_BASELINE_TASK_LIST_FILTERS_SUCCESS) {
				const formFilters = {
					isStartDateActive: payload.isStartDateActive,
					isTaskStepsActive: payload.isTaskStepsActive,
					isTaskEffortActive: payload.isTaskEffortActive,
					isTaskFeedbackActive: payload.isTaskFeedbackActive,
					isTaskDependenciesActive: payload.isTaskDependenciesActive,
					isProjectWidgetActive: payload.isProjectWidgetActive,
					isWebhooksActive: payload.isWebhooksActive,
					isBaselineTaskValidationRulesActive: payload.isBaselineTaskValidationRulesActive,
				};

				this.setState({
					departments: payload.departments,
					baselineJobTitles: payload.baselineJobTitles,
					communities: payload.communities,
					phases: payload.phases,
					disciplines: payload.disciplines,
					tags: payload.tags,
					formFilters: {
						...this.state.formFilters,
						...formFilters,
					},
				});
			} else if (result.type === types.LOAD_BASELINE_TASK_LIST_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			this.setState({ isFetchingFilters: false });
		}
	};

	loadFormFilters = async () => {
		// Only called the first time the drawer is opened
		// the results are then certifiedd here in the controller for a next time the drawer opens, instead of having the drawer keep loading them over and over
		try {
			const result = await this.props.baselineTaskActions.loadBaselineTaskFormFilters(
				this.props.subscriptionId,
				this.state.searchParams.baselineId
			);
			const payload = result.payload;
			if (result.type === types.LOAD_BASELINE_TASK_FORM_FILTERS_SUCCESS) {
				const formFilters = {
					isLoaded: true,
					departments: payload.departments,
					baselineJobTitles: payload.baselineJobTitles,
					communities: payload.communities,
					phases: payload.phases,
					certifiedTaskId: payload.certifiedTaskId,
					subscriptionFiles: payload.subscriptionFiles,
					taskTags: payload.taskTags,
					workflows: payload.workflows,
				};

				const newFilters = { ...this.state.formFilters, ...formFilters };
				this.setState({ formFilters: newFilters });
			} else if (result.type === types.LOAD_BASELINE_TASK_FORM_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	async loadBaselines() {
		try {
			this.setState({ isFetchingBaselines: true });
			const result = await this.props.baselineActions.loadList(this.props.subscriptionId);
			const payload = result.payload;
			if (result.type === types.LOAD_BASELINE_LIST_SUCCESS) {
				this.setState({ baselines: payload.baselines });

				// if there is a baselineId on the URL
				if (this.props.urlParams.baselineId) {
					// make sure it is in the results, and select it
					const baseline = payload.baselines.find((x) => x.id.toString() === this.props.urlParams.baselineId);
					if (baseline) {
						this.setState(
							{
								searchParams: {
									...this.state.searchParams,
									baselineId: this.props.urlParams.baselineId,
								},
							},
							() => {
								this.search();
							}
						);
					} else {
						// if there was a baselineId passed in but not found in the active baselines for the sub (in the payload)
						Notification('error', 'Baseline Not Found', 'Unable to find baseline for url parameter');
					}
				}
			} else if (result.type === types.LOAD_BASELINE_LIST_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			this.setState({ isFetchingBaselines: false });
		}
	}

	parseMultiSelectColumnFilters = () => {
		const fields = ['departments', 'baselineJobTitles', 'communities', 'phases', 'disciplines', 'tags'];

		const result = {};

		//If the field contains and id, we return that id. Otherwise we just return
		// the array as it is
		fields.forEach((field) => {
			const values = this.state.searchParams[field] || [];
			result[field] = values.map((x) => (x && typeof x === 'object' ? x.id : x));
		});

		return result;
	};

	async search() {
		try {
			// determine if mobile in case window was resized after mount
			// uses same params for search, but returns different result objects based on desktop/mobile
			// (mobile only displays a few things, while desktop has many column options)
			this.setState({ isFetchingTasks: true });
			const columnFilters = this.parseMultiSelectColumnFilters();
			const request = {
				...this.state.searchParams,
				...columnFilters,
			};

			const result = await this.props.baselineTaskActions.searchBaselineTasks(request);
			const payload = result.payload;
			if (result.type === types.SEARCH_BASELINE_TASK_SUCCESS) {
				const tasks = payload.results.map((item) => {
					return { ...item, IsChecked: false };
				});
				this.setState({
					baselineTasks: tasks,
					columnOrder: payload.columnOrder,
					totalCount: payload.totalCount,
				});

				if (this.props.urlParams.baselineTaskId) {
					const baselineTaskId = parseInt(this.props.urlParams.baselineTaskId);
					this.showTask(baselineTaskId);
				}
			} else if (result.type === types.SEARCH_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			this.setState({ isFetchingTasks: false });
		}
	}

	//------ functions called by desktop/mobile components ------
	updateSearchParams = (name, value) => {
		this.setState({
			lastFilterChange: new Date(),
			searchParams: { ...this.state.searchParams, [name]: value },
		});
	};

	updateParamsAndSearch = (name, value) => {
		if (name) {
			this.setState(
				{
					lastFilterChange: new Date(),
					searchParams: { ...this.state.searchParams, [name]: value },
				},
				async () => {
					if (name === 'baselineId') {
						this.props.history.push(Routes.BaselineTaskList + '/?bid=' + value);
						await this.loadListFilters();
						await this.clearFilters();
					} else {
						this.search();
					}
				}
			);
		} else {
			// If no params passed in, just search
			this.search();
		}
	};

	updateCreatedDateParams = (start, end) => {
		this.setState(
			{
				searchParams: {
					lastFilterChange: new Date(),
					...this.state.searchParams,
					createdDateStart: start,
					createdDateEnd: end,
				},
			},
			() => {
				this.search();
			}
		);
	};

	onViewSelectorChange = (viewName) => {
		this.setState(
			{
				lastFilterChange: new Date(),
				searchParams: {
					...this.state.searchParams,
					pageSize: DEFAULT_PAGE_SIZE,
					pageNumber: 1,
				},
				selectedView: viewName,
			},
			() => {
				if (viewName === VIEWS.List.value) {
					this.search();
				}
			}
		);
	};

	onViewFiltersComplete = (newParams) => {
		this.setState(
			{
				showFiltersDrawer: false,
				lastFilterChange: new Date(),
				searchParams: newParams,
			},
			() => {
				if (this.state.selectedView === VIEWS.List.value) {
					this.search();
				}
			}
		);
	};

	onViewFiltersCancel = () => {
		this.setState({ showFiltersDrawer: false });
	};

	onViewFiltersClick = () => {
		this.setState({ showFiltersDrawer: true });
	};

	hasNoFilters = () => {
		const initParamsWithBaselineId = {
			...this.state.initialSearchParams,
			baselineId: this.state.searchParams.baselineId,
		};
		return _.isEqual(initParamsWithBaselineId, this.state.searchParams);
	};

	clearFilters = () => {
		const initParamsWithBaselineId = {
			...this.state.initialSearchParams,
			baselineId: this.state.searchParams.baselineId,
		};
		this.setState({ searchParams: initParamsWithBaselineId, lastFilterChange: new Date() }, this.search);
	};

	onToggleCheckedTask = (baselineTaskId) => {
		const tasks = this.state.baselineTasks;
		let checkedTasks = [];

		const newTasks = tasks.map((item) => {
			let task = { ...item };
			if (item.id === baselineTaskId) {
				task.IsChecked = !item.IsChecked;
			}

			if (task.IsChecked) {
				checkedTasks.push(task);
			}
			return task;
		});

		// If we've manually checked all tasks, also check the master checkbox
		const allTasksChecked = newTasks.length === checkedTasks.length;

		this.setState({
			checkedTasks: checkedTasks,
			baselineTasks: newTasks,
			allTasksChecked: allTasksChecked,
		});
	};

	onMasterCheckboxToggle = () => {
		// Toggle the master checkbox to checked or unchecked
		const allTasksChecked = !this.state.allTasksChecked;

		// Check or uncheck all baseline tasks, based on its newly-toggled state
		const newTasks = this.state.baselineTasks.map((item) => {
			return { ...item, IsChecked: allTasksChecked };
		});
		const checkedTasks = allTasksChecked ? newTasks.slice() : [];

		this.setState({
			checkedTasks: checkedTasks,
			baselineTasks: newTasks,
			allTasksChecked: allTasksChecked,
		});
	};

	showTask = async (id) => {
		if (!this.state.formFilters.isLoaded) {
			await this.loadFormFilters();
		}

		this.setState({ editTaskId: id, isDrawerOpen: true, isFormValid: true });
	};

	clearAllCheckedTasks = () => {
		const baselineTasks = this.state.baselineTasks.map((item) => {
			return { ...item, IsChecked: false };
		});
		this.setState({
			checkedTasks: [],
			allTasksChecked: false,
			baselineTasks: baselineTasks,
		});
	};

	//------ functions called by create/edit drawer ------

	onDrawerComplete = () => {
		this.setState({ isDrawerOpen: false, lastFilterChange: new Date() });
		this.clearAllCheckedTasks();
		this.search();
		this.loadFormFilters();
	};

	onDrawerCancel = () => {
		this.setState({ isDrawerOpen: false });
	};

	onColumnSort = async (sortBy, sortDir) => {
		// Only search again if the values have changed
		if (this.state.searchParams.sortBy !== sortBy || this.state.searchParams.sortDir !== sortDir) {
			// Once a column has been clicked, the default multi-column sorting is no longer valid, so clear it out.
			this.setState(
				{
					searchParams: {
						...this.state.searchParams,
						sortBy: sortBy,
						sortDir: sortDir,
						multiColumnSort: [],
					},
				},
				this.search
			);
		}
	};

	render() {
		return (
			<div className='baseline-task-list-page'>
				<DocumentTitle title='Baseline Task List' />
				{this.isMobile() ? (
					<BaselineTaskListMobile
						baselines={this.state.baselines}
						baselineTasks={this.state.baselineTasks}
						isFetchingBaselines={this.state.isFetchingBaselines}
						isFetchingTasks={this.state.isFetchingTasks}
						updateParamsAndSearch={this.updateParamsAndSearch}
						updateSearchParams={this.updateSearchParams}
						searchParams={this.state.searchParams}
						departments={this.state.departments}
						baselineJobTitles={this.state.baselineJobTitles}
						communities={this.state.communities}
						phases={this.state.phases}
						disciplines={this.state.disciplines}
						showTask={this.showTask}
						formFilters={this.state.formFilters}
						search={this.search}
						onViewFiltersClick={this.onViewFiltersClick}
					/>
				) : (
					!this.state.isFetchingTasks && (
						<BaselineTaskListDesktop
							subscriptionId={this.props.subscriptionId}
							baselines={this.state.baselines}
							columnOrder={this.state.columnOrder}
							baselineActions={this.props.baselineActions}
							filesActions={this.props.filesActions}
							baselineTasks={this.state.baselineTasks}
							baselineTaskActions={this.props.baselineTaskActions}
							totalCount={this.state.totalCount}
							isFetchingBaselines={this.state.isFetchingBaselines}
							isFetchingTasks={this.state.isFetchingTasks}
							isFetchingFilters={this.state.isFetchingFilters}
							updateSearchParams={this.updateSearchParams}
							updateParamsAndSearch={this.updateParamsAndSearch}
							searchParams={this.state.searchParams}
							selectTask={this.selectTask}
							departments={this.state.departments}
							baselineJobTitles={this.state.baselineJobTitles}
							communities={this.state.communities}
							phases={this.state.phases}
							disciplines={this.state.disciplines}
							tags={this.state.tags}
							showTask={this.showTask}
							onToggleCheckedTask={this.onToggleCheckedTask}
							onMasterCheckboxToggle={this.onMasterCheckboxToggle}
							allTasksChecked={this.state.allTasksChecked}
							formFilters={this.state.formFilters}
							checkedTasks={this.state.checkedTasks}
							onBulkPushdownComplete={this.onDrawerComplete}
							onComplete={this.onDrawerComplete}
							hasNoFilters={this.hasNoFilters}
							clearFilters={this.clearFilters}
							onSort={this.onColumnSort}
							updateCreatedDateParams={this.updateCreatedDateParams}
							onViewSelectorChange={this.onViewSelectorChange}
							onViewFiltersClick={this.onViewFiltersClick}
							lastFilterChange={this.state.lastFilterChange}
							selectedView={this.state.selectedView}
							taskTagActions={this.props.taskTagActions}
						/>
					)
				)}
				{this.state.isDrawerOpen && (
					<BaselineTaskDrawer
						subscriptionId={this.props.subscriptionId}
						baselineId={parseInt(this.state.searchParams.baselineId)}
						taskId={this.state.editTaskId}
						onComplete={this.onDrawerComplete}
						onCancel={this.onDrawerCancel}
						onUpdateList={this.updateParamsAndSearch}
						formFilters={this.state.formFilters}
						baselineTaskActions={this.props.baselineTaskActions}
						isMobile={this.isMobile()}
					/>
				)}
				{this.state.showFiltersDrawer && (
					<FiltersDrawer
						onComplete={this.onViewFiltersComplete}
						onCancel={this.onViewFiltersCancel}
						searchParams={this.state.searchParams}
						updateSearchParams={this.state.updateSearchParams}
						departments={this.state.departments}
						baselineJobTitles={this.state.baselineJobTitles}
						communities={this.state.communities}
						phases={this.state.phases}
						disciplines={this.state.disciplines}
					/>
				)}
			</div>
		);
	}
}

BaselineTaskListController.propTypes = {
	windowWidth: PropTypes.number,
	subscriptionId: PropTypes.string.isRequired,
	windowActions: PropTypes.object.isRequired,
	baselineTaskActions: PropTypes.object.isRequired,
	taskTagActions: PropTypes.object.isRequired,
	baselineActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	history: PropTypes.object.isRequired,
	urlParams: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
	let urlParams = {
		baselineId: '',
		baselineTaskId: '',
	};

	const searchParams = new URLSearchParams(ownProps.location.search);

	if (searchParams.has(URLParams.BaselineId)) {
		urlParams.baselineId = searchParams.get(URLParams.BaselineId);
	}
	if (searchParams.has(URLParams.BaselineTaskId)) {
		urlParams.baselineTaskId = searchParams.get(URLParams.BaselineTaskId);
	}

	return {
		windowWidth: state.window.width,
		subscriptionId: state.user.currentSubscriptionId.toString(),
		history: ownProps.history,
		urlParams: urlParams,
	};
}

function mapDispatchTpProps(dispatch) {
	return {
		baselineTaskActions: bindActionCreators(baselineTaskActions, dispatch),
		baselineActions: bindActionCreators(baselineActions, dispatch),
		filesActions: bindActionCreators(filesActions, dispatch),
		windowActions: bindActionCreators(windowActions, dispatch),
		taskTagActions: bindActionCreators(taskTagActions, dispatch),
	};
}

export default connect(mapStateToProps, mapDispatchTpProps)(BaselineTaskListController);
